import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, AlertTriangle, AlertCircle, Info, Search } from "lucide-react";
import {
  verifyOpenDetection,
  listVerifiableCampaigns,
  type OpenVerificationResult,
  type DiagnosticReason,
} from "@/lib/open-verification.functions";

export const Route = createFileRoute("/_authenticated/open-verification")({
  component: OpenVerificationPage,
  head: () => ({ meta: [{ title: "Open-Verifizierung — InboxPulse" }] }),
});

function OpenVerificationPage() {
  const run = useServerFn(verifyOpenDetection);
  const listFn = useServerFn(listVerifiableCampaigns);

  const [campaignId, setCampaignId] = useState("");
  const [shortToken, setShortToken] = useState("");
  const [windowHours, setWindowHours] = useState(24);

  const list = useQuery({
    queryKey: ["verifiable-campaigns"],
    queryFn: () => listFn({ data: { limit: 100 } }),
  });

  const mutation = useMutation({
    mutationFn: () =>
      run({
        data: {
          campaignId: campaignId || undefined,
          shortToken: shortToken || undefined,
          windowHours,
        },
      }),
  });

  const result = mutation.data;

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-6">
      <PageHeader
        title="Open-Erkennungs-Verifizierung"
        description="Prüft, ob Öffnungen ausschließlich über den unsichtbaren Pixel gezählt werden (keine Klick-Trigger) und nennt für fehlende Events konkrete Gründe wie Dedup, fehlende Parameter oder Cache/Blockierung."
      />

      <HelpPanel
        title="Hilfe: Funktioniert die Öffnungs-Erkennung wirklich?"
        what={
          <>
            Diese Seite ist ein <b>Selbst-Test</b>. Sie schaut nach: "Werden Öffnungen wirklich
            erkannt? Und werden sie aus den richtigen Gründen gezählt (nicht durch einen Klick)?"
          </>
        }
        why={
          <>
            Manchmal blockt Gmail/Apple das Tracking-Bild oder Filter zählen Klicks fälschlich als
            Öffnung. Hier siehst du sofort, ob deine Statistik vertrauenswürdig ist.
          </>
        }
        how={[
          <>Seite öffnen — Test läuft automatisch im Hintergrund.</>,
          <>Grüne Häkchen = alles ok. Rote Hinweise = einer der genannten Gründe trifft zu.</>,
          <>
            Bei Problemen: Hinweis lesen — meist fehlt ein URL-Parameter oder das Bild wurde vom
            Anbieter gecacht.
          </>,
        ]}
        tips={[
          <>
            Apple Mail Privacy Protection lädt Bilder vorab → Öffnung wird gezählt, auch wenn
            niemand die Mail wirklich gelesen hat. Normal.
          </>,
          <>Gmail-Cache: Öffnungen werden manchmal stundenlang verspätet gezählt. Auch normal.</>,
        ]}
        glossary={[
          { term: "Dedup", def: "Doppelte Öffnungen vom selben Empfänger werden nur 1x gezählt." },
          {
            term: "Cache",
            def: "Zwischenspeicher. Gmail/Apple speichern Bilder kurz, um Daten zu sparen — das verzögert die Zählung.",
          },
          { term: "Pixel", def: "Das winzige Tracking-Bild in deiner Mail." },
        ]}
      />

      <Card className="p-5">
        <form
          className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end"
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate();
          }}
        >
          <div className="md:col-span-5">
            <label className="text-xs text-muted-foreground">Kampagne wählen</label>
            <select
              className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
              value={campaignId}
              onChange={(e) => {
                setCampaignId(e.target.value);
                setShortToken("");
              }}
            >
              <option value="">— Kampagne auswählen —</option>
              {(list.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} · gesendet: {c.sent_count} · Opens: {c.open_count}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-3">
            <label className="text-xs text-muted-foreground">…oder 8-stelliges Kürzel</label>
            <Input
              value={shortToken}
              maxLength={8}
              placeholder="z.B. bt6xadgw"
              onChange={(e) => {
                setShortToken(e.target.value.toLowerCase().trim());
                if (e.target.value) setCampaignId("");
              }}
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs text-muted-foreground">Zeitfenster (h)</label>
            <Input
              type="number"
              min={1}
              max={720}
              value={windowHours}
              onChange={(e) => setWindowHours(Number(e.target.value) || 24)}
            />
          </div>
          <div className="md:col-span-2">
            <Button
              type="submit"
              className="w-full"
              disabled={mutation.isPending || (!campaignId && !shortToken)}
            >
              <Search className="size-4 mr-2" />
              {mutation.isPending ? "Prüfe…" : "Verifizieren"}
            </Button>
          </div>
        </form>
        {mutation.error instanceof Error && (
          <p className="mt-3 text-sm text-destructive">{mutation.error.message}</p>
        )}
      </Card>

      {result && <ResultView result={result} />}
    </div>
  );
}

function ResultView({ result }: { result: OpenVerificationResult }) {
  const t = result.totals;
  const sourcePure = t.openEvents > 0 && t.nonPixelOpenEvents === 0;

  return (
    <>
      <Card className="p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-lg font-semibold">{result.campaign.name}</h3>
            <p className="text-xs text-muted-foreground font-mono">
              {result.campaign.id} · Kürzel: {result.campaign.shortToken ?? "—"} · gesendet:{" "}
              {result.campaign.sent_count}
            </p>
          </div>
          <Badge
            variant={sourcePure ? "default" : t.openEvents === 0 ? "secondary" : "destructive"}
            className="text-sm"
          >
            {sourcePure
              ? "✓ Nur Pixel-Quelle"
              : t.openEvents === 0
                ? "Noch keine Events"
                : `⚠ ${t.nonPixelOpenEvents} Nicht-Pixel-Öffnungen`}
          </Badge>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
          <Stat label="Pixel-Opens" value={t.pixelOpenEvents} accent="primary" />
          <Stat
            label="Nicht-Pixel-Opens"
            value={t.nonPixelOpenEvents}
            accent={t.nonPixelOpenEvents > 0 ? "danger" : "muted"}
          />
          <Stat label="Eindeutige Empfänger (Hash)" value={t.distinctRecipients} />
          <Stat label="Andere Events (≠ open)" value={t.nonOpenEvents} accent="muted" />
          <Stat label="Dedup zusammengeführt" value={t.duplicatesCollapsed} accent="muted" />
          <Stat
            label="Queue fehlgeschlagen"
            value={t.failedQueue}
            accent={t.failedQueue > 0 ? "danger" : "muted"}
          />
          <Stat label="Queue ausstehend" value={t.pendingQueue} accent="muted" />
          <Stat
            label="Öffnungsrate"
            value={
              result.campaign.sent_count > 0
                ? `${((t.pixelOpenEvents / result.campaign.sent_count) * 100).toFixed(1)} %`
                : "—"
            }
          />
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card className="p-5">
          <h4 className="font-semibold mb-3">Diagnose & mögliche Gründe</h4>
          <div className="space-y-2">
            {result.reasons.length === 0 && (
              <p className="text-sm text-muted-foreground">Keine Auffälligkeiten erkannt.</p>
            )}
            {result.reasons.map((r, i) => (
              <ReasonRow key={i} reason={r} />
            ))}
          </div>
        </Card>

        <Card className="p-5">
          <h4 className="font-semibold mb-3">Quellen & Provider</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs text-muted-foreground mb-1">Quelle (metadata.source)</div>
              <ul className="text-sm space-y-1">
                {result.bySource.length === 0 && <li className="text-muted-foreground">—</li>}
                {result.bySource.map((s) => (
                  <li key={s.source} className="flex justify-between">
                    <span className="font-mono">{s.source}</span>
                    <span>{s.count}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">Provider (User-Agent)</div>
              <ul className="text-sm space-y-1">
                {result.byProvider.length === 0 && <li className="text-muted-foreground">—</li>}
                {result.byProvider.map((p) => (
                  <li key={p.provider} className="flex justify-between">
                    <span>{p.provider}</span>
                    <span>{p.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Card>
      </div>

      <Card className="p-5">
        <h4 className="font-semibold mb-3">Letzte Pixel-Öffnungen (max. 25)</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b">
                <th className="py-2 pr-3">Zeit</th>
                <th className="py-2 pr-3">Quelle</th>
                <th className="py-2 pr-3">Provider</th>
                <th className="py-2 pr-3">Device</th>
                <th className="py-2 pr-3">Empfänger</th>
                <th className="py-2 pr-3">Absender</th>
                <th className="py-2 pr-3">Request-ID</th>
              </tr>
            </thead>
            <tbody>
              {result.samples.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-4 text-center text-muted-foreground">
                    Keine Events im Fenster.
                  </td>
                </tr>
              )}
              {result.samples.map((s) => (
                <tr key={s.id} className="border-b last:border-0">
                  <td className="py-2 pr-3 font-mono text-xs">
                    {new Date(s.occurred_at).toLocaleString()}
                  </td>
                  <td className="py-2 pr-3">
                    <Badge
                      variant={
                        s.source === "i-pixel" || s.source === "q-pixel" ? "default" : "destructive"
                      }
                    >
                      {s.source ?? "—"}
                    </Badge>
                  </td>
                  <td className="py-2 pr-3">{s.provider ?? "—"}</td>
                  <td className="py-2 pr-3">{s.device ?? "—"}</td>
                  <td className="py-2 pr-3 font-mono text-xs">
                    {s.recipientLabel ?? s.recipientHash?.slice(0, 12) ?? "—"}
                  </td>
                  <td className="py-2 pr-3 font-mono text-xs">{s.senderLabel ?? "—"}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{s.request_id ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: number | string;
  accent?: "primary" | "danger" | "muted";
}) {
  const color =
    accent === "primary"
      ? "text-primary"
      : accent === "danger"
        ? "text-destructive"
        : accent === "muted"
          ? "text-muted-foreground"
          : "text-foreground";
  return (
    <div className="rounded-md border border-border p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-2xl font-semibold ${color}`}>{value}</div>
    </div>
  );
}

function ReasonRow({ reason }: { reason: DiagnosticReason }) {
  const Icon =
    reason.severity === "error"
      ? AlertCircle
      : reason.severity === "warn"
        ? AlertTriangle
        : reason.code === "ok"
          ? CheckCircle2
          : Info;
  const color =
    reason.severity === "error"
      ? "text-destructive"
      : reason.severity === "warn"
        ? "text-yellow-600 dark:text-yellow-500"
        : reason.code === "ok"
          ? "text-green-600 dark:text-green-500"
          : "text-muted-foreground";
  return (
    <div className="flex gap-3 rounded-md border border-border p-3">
      <Icon className={`size-5 shrink-0 mt-0.5 ${color}`} />
      <div className="flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm">{reason.title}</span>
          {typeof reason.count === "number" && (
            <Badge variant="secondary" className="text-xs">
              {reason.count}
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{reason.detail}</p>
      </div>
    </div>
  );
}
